import React, { useRef, useState, useCallback } from 'react';
import DataGrid, {
  Column,
  Selection,
  Scrolling,
  SearchPanel,
  ColumnChooser,
  Export,
  Toolbar,
  Item,
  LoadPanel,
  FilterPanel,
  FilterBuilderPopup,
  DataGridRef,
  HeaderFilter,
  FilterRow,
  Sorting
} from 'devextreme-react/data-grid';
import Button from 'devextreme-react/button';
import DropDownButton from 'devextreme-react/drop-down-button';
import DateBox from 'devextreme-react/date-box';
import './CustomDataGrid.css';

interface ColumnConfig {
  dataField: string;
  caption: string;
  width?: number;
  dataType?: 'string' | 'number' | 'date';
  alignment?: 'left' | 'center' | 'right';
  cellRender?: (data: any) => React.ReactNode;
}

interface CustomDataGridProps {
  // Zorunlu
  data: any[];
  columns: ColumnConfig[];
  keyExpr?: string;

  // Seçim
  selectedRowKeys?: any[];
  onSelectionChanged?: (keys: any[]) => void;

  // Arama
  searchPlaceholder?: string;

  // Tarih filtreleri (DateBox için)
  dateFilters?: Array<{
    value: Date | null;
    onValueChanged: (date: Date | null) => void;
    displayFormat?: string;
    width?: number;
  }>;

  // Butonlar
  onRefresh?: () => void;
  onAddNew?: () => void;
  onDelete?: (keys: any[]) => void;
  onExport?: (type: string, format: string) => void;
  onStatusFilter?: (status: string) => void;

  // Export seçenekleri
  exportEnabled?: boolean;
  exportOptions?: Array<{ id: number; text: string; icon?: string }>;

  // Status filter
  statusFilterText?: string;
  statusFilterOptions?: Array<{ id: number; text: string }>;

  // Görünüm
  className?: string;
  height?: string;

  // Translations
  t?: (key: string) => string;

  // No data text
  noDataText?: string;

  // Row click handler
  onRowClick?: (data: any) => void;

  // Content ready handler
  onContentReady?: (e: any) => void;

  // Additional toolbar items
  additionalToolbarItems?: Array<{
    location: 'before' | 'after' | 'center';
    render?: () => React.ReactNode;
    widget?: string;
    options?: any;
  }>;

  // Column chooser
  columnChooserEnabled?: boolean;

  // Filter row
  filterRowVisible?: boolean;

  // Allow column reordering
  allowColumnReordering?: boolean;

  // Row alternation
  rowAlternationEnabled?: boolean;
}

export const CustomDataGrid: React.FC<CustomDataGridProps> = ({
  data,
  columns,
  keyExpr = 'id',
  selectedRowKeys,
  onSelectionChanged,
  searchPlaceholder = 'Ara...',
  dateFilters,
  onRefresh,
  onAddNew,
  onDelete,
  onExport,
  onStatusFilter,
  exportEnabled = false,
  exportOptions,
  statusFilterText,
  statusFilterOptions,
  className = 'custom-grid compact-grid',
  height = '100%',
  t = (key: string) => key,
  noDataText = 'Veri bulunamadı',
  onRowClick,
  onContentReady,
  additionalToolbarItems,
  columnChooserEnabled = true,
  filterRowVisible = false,
  allowColumnReordering = true,
  rowAlternationEnabled = true,
}) => {
  const gridRef = useRef<DataGridRef>(null);
  const [showFilterRow, setShowFilterRow] = useState(filterRowVisible);
  const [rowCount, setRowCount] = useState(0);
  const rowCountRef = useRef<HTMLSpanElement>(null);

  const updateRowCountDisplay = useCallback((count: number) => {
    if (rowCountRef.current) {
      rowCountRef.current.textContent = `${count} ${t("records")}`;
    }
  }, [t]);

  const handleContentReady = useCallback((e: any) => {
    const count = e.component.totalCount();
    updateRowCountDisplay(count);
    if (rowCount === 0) {
      setRowCount(count);
    }
    onContentReady?.(e);
  }, [rowCount, updateRowCountDisplay, onContentReady]);

  return (
    <DataGrid
      className={className}
      height={height}
      focusedRowEnabled
      keyExpr={keyExpr}
      dataSource={data}
      onRowClick={onRowClick ? (e) => onRowClick(e.data) : undefined}
      noDataText={noDataText}
      onContentReady={handleContentReady}
      onSelectionChanged={onSelectionChanged ? (e) => onSelectionChanged(e.selectedRowKeys) : undefined}
      allowColumnReordering={allowColumnReordering}
      rowAlternationEnabled={rowAlternationEnabled}
      allowColumnResizing
      columnResizingMode="widget"
      showBorders
      ref={gridRef}
    >
      <LoadPanel showPane={false} text={t("loading")} />
      <SearchPanel visible placeholder={searchPlaceholder} />
      {columnChooserEnabled && <ColumnChooser title={t("columnChooser")} enabled />}
      <FilterRow visible={showFilterRow} applyFilter="auto" />
      {exportEnabled && <Export enabled allowExportSelectedData formats={['xlsx', 'pdf']} />}
      {onSelectionChanged && (
        <Selection selectAllMode="allPages" showCheckBoxesMode="always" mode="multiple" />
      )}
      <FilterPanel visible={true} />
      <FilterBuilderPopup width="50%" height="50%" title={t("filterBuilder")} />
      <HeaderFilter visible />
      <Sorting mode="multiple" />
      <Scrolling mode="virtual" />

      <Toolbar>
        <Item name="searchPanel" location="before" />

        <Item location="before">
          <Button
            icon="endswith"
            hint={t("detailedSearch")}
            stylingMode={showFilterRow ? "contained" : "text"}
            onClick={() => setShowFilterRow(!showFilterRow)}
          />
        </Item>

        {columnChooserEnabled && <Item name="columnChooserButton" location="before" />}

        {exportEnabled && !exportOptions && <Item name="exportButton" location="before" />}

        {/* Custom Export Dropdown */}
        {onExport && exportOptions && exportOptions.length > 0 && (
          <Item location="before">
            <DropDownButton
              icon="export"
              stylingMode="text"
              hint={t("export")}
              displayExpr="text"
              keyExpr="id"
              items={exportOptions}
              dropDownOptions={{ width: 250 }}
              onItemClick={(e: any) => {
                const itemId = e.itemData.id;
                if (itemId === 1) onExport('selected', 'excel');
                else if (itemId === 2) onExport('all', 'excel');
                else if (itemId === 3) onExport('selected', 'pdf');
                else if (itemId === 4) onExport('all', 'pdf');
              }}
            />
          </Item>
        )}

        {onRefresh && (
          <Item location="before" showText="inMenu">
            <Button
              icon="refresh"
              hint={t("refresh")}
              stylingMode="text"
              onClick={onRefresh}
            />
          </Item>
        )}

        <Item location="before">
          <div className="hidden md:block separator" />
        </Item>

        {/* Status Filter Dropdown */}
        {onStatusFilter && statusFilterOptions && statusFilterOptions.length > 0 && (
          <Item location="before" locateInMenu="auto">
            <DropDownButton
              items={statusFilterOptions.map(opt => opt.text)}
              stylingMode="text"
              text={statusFilterText || t("status")}
              dropDownOptions={{ width: 'auto' }}
              useSelectMode
              onSelectionChanged={(e: any) => {
                if (e.item) {
                  onStatusFilter(e.item);
                }
              }}
            />
          </Item>
        )}

        {/* Date Filters */}
        {dateFilters && dateFilters.map((dateFilter, index) => (
          <Item key={`date-${index}`} location="before" locateInMenu="auto">
            <DateBox
              value={dateFilter.value || undefined}
              onValueChanged={(e) => dateFilter.onValueChanged(e.value || null)}
              displayFormat={dateFilter.displayFormat || 'dd.MM.yyyy'}
              type="date"
              width={dateFilter.width || 165}
              stylingMode="outlined"
              showClearButton={true}
            />
          </Item>
        ))}

        {/* Delete Selected Button */}
        {onDelete && selectedRowKeys && selectedRowKeys.length > 0 && (
          <Item location="before">
            <DropDownButton
              text={`(${selectedRowKeys.length}) ${t("transactions")}`}
              icon="menu"
              width={175}
              items={[
                { text: t("deleteSelected"), icon: 'trash', action: 'deleteSelected' },
              ]}
              onItemClick={(e: any) => {
                if (e.itemData.action === "deleteSelected") {
                  onDelete(selectedRowKeys);
                }
              }}
            />
          </Item>
        )}

        {/* Additional Toolbar Items */}
        {additionalToolbarItems && additionalToolbarItems.map((item, index) => (
          <Item key={`additional-${index}`} location={item.location} locateInMenu="auto">
            {item.render ? item.render() : null}
          </Item>
        ))}

        {/* Row Count */}
        <Item location="center" locateInMenu="auto">
          <span ref={rowCountRef}>{rowCount} {t("records")}</span>
        </Item>

        {/* Add New Button */}
        {onAddNew && (
          <Item location="after" locateInMenu="auto">
            <Button
              icon="plus"
              text=""
              type="default"
              stylingMode="contained"
              onClick={onAddNew}
            />
          </Item>
        )}
      </Toolbar>

      {columns.map((col: ColumnConfig, index: number) => (
        <Column
          key={index}
          dataField={col.dataField}
          caption={col.caption}
          width={col.width}
          dataType={col.dataType}
          alignment={col.alignment}
          cellRender={col.cellRender}
        />
      ))}
    </DataGrid>
  );
};

export default CustomDataGrid;
